import React, { useState, useEffect } from 'react';
import { 
    collection, query, onSnapshot, doc, addDoc, updateDoc, deleteDoc, serverTimestamp, orderBy, writeBatch 
} from 'firebase/firestore';
import { db } from '../App';
import { appId } from '../config/firebaseConfig';

export default function ServiceValuesPage({ permissionLevel }) {
    const [services, setServices] = useState([]);
    const [categoriesList, setCategoriesList] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [categoryFilter, setCategoryFilter] = useState('Todas');
    
    const [isServiceModalOpen, setIsServiceModalOpen] = useState(false);
    const [isCatModalOpen, setIsCatModalOpen] = useState(false);
    
    const [editingService, setEditingService] = useState(null);
    const [newCatName, setNewCatName] = useState('');
    
    const [formData, setFormData] = useState({
        name: '',
        description: '',
        category: '',
        value: '',
        period: 'Único'
    });

    const canEdit = permissionLevel === 'edit';
    const collectionPath = `/artifacts/${appId}/public/data/service_values`;
    const categoriesPath = `/artifacts/${appId}/public/data/service_categories`;

    // Listener para Serviços (Ordenação via Javascript para evitar filtros do Firestore)
    useEffect(() => {
        const q = query(collection(db, collectionPath));
        const unsubscribe = onSnapshot(q, (snapshot) => {
            const data = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            // Ordena por posição, se não houver posição usa -1 (migração tratará disso)
            data.sort((a, b) => {
                const posA = a.position !== undefined ? a.position : -1;
                const posB = b.position !== undefined ? b.position : -1;
                if (posA === -1 || posB === -1) return a.name.localeCompare(b.name);
                return posA - posB;
            });
            setServices(data);
            setLoading(false);
        }, (error) => {
            console.error("Erro no listener de serviços:", error);
            setLoading(false);
        });
        return () => unsubscribe();
    }, []);

    // Migração: Atribuir posições se não existirem
    useEffect(() => {
        if (!canEdit || loading || services.length === 0) return;
        
        const migratePositions = async () => {
            const itemsToUpdate = services.filter(s => s.position === undefined);
            if (itemsToUpdate.length === 0) return;

            const batch = writeBatch(db);
            services.forEach((service, index) => {
                if (service.position === undefined) {
                    const docRef = doc(db, collectionPath, service.id);
                    batch.update(docRef, { position: index });
                }
            });
            try {
                await batch.commit();
            } catch (e) {
                console.error("Erro na migração de posições:", e);
            }
        };
        migratePositions();
    }, [services, canEdit, loading]);

    // Listener para Categorias
    useEffect(() => {
        const q = query(collection(db, categoriesPath));
        const unsubscribe = onSnapshot(q, (snapshot) => {
            const data = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            data.sort((a, b) => a.name.localeCompare(b.name));
            setCategoriesList(data);
        });
        return () => unsubscribe();
    }, []);

    const handleOpenServiceModal = (service = null) => {
        if (service) {
            setEditingService(service);
            setFormData({
                name: service.name,
                description: service.description,
                category: service.category || '',
                value: service.value,
                period: service.period || 'Único'
            });
        } else {
            setEditingService(null);
            setFormData({ 
                name: '', 
                description: '', 
                category: categoriesList.length > 0 ? categoriesList[0].name : '', 
                value: '',
                period: 'Único'
            });
        }
        setIsServiceModalOpen(true);
    };

    const handleSaveService = async (e) => {
        e.preventDefault();
        try {
            if (editingService) {
                const docRef = doc(db, collectionPath, editingService.id);
                await updateDoc(docRef, { ...formData, updatedAt: serverTimestamp() });
            } else {
                // Novo serviço vai para o final da fila
                const nextPosition = services.length > 0 ? Math.max(...services.map(s => s.position || 0)) + 1 : 0;
                await addDoc(collection(db, collectionPath), { 
                    ...formData, 
                    position: nextPosition,
                    createdAt: serverTimestamp() 
                });
            }
            setIsServiceModalOpen(false);
        } catch (error) {
            console.error("Erro ao salvar serviço:", error);
            alert("Erro ao salvar serviço.");
        }
    };

    const handleMove = async (index, direction) => {
        const newIndex = direction === 'up' ? index - 1 : index + 1;
        if (newIndex < 0 || newIndex >= services.length) return;

        const batch = writeBatch(db);
        const currentItem = services[index];
        const neighborItem = services[newIndex];

        const currentRef = doc(db, collectionPath, currentItem.id);
        const neighborRef = doc(db, collectionPath, neighborItem.id);

        batch.update(currentRef, { position: neighborItem.position });
        batch.update(neighborRef, { position: currentItem.position });

        try {
            await batch.commit();
        } catch (e) {
            console.error("Erro ao mover item:", e);
        }
    };

    const handleSaveCategory = async (e) => {
        e.preventDefault();
        if (!newCatName.trim()) return;
        try {
            await addDoc(collection(db, categoriesPath), { 
                name: newCatName.trim(), 
                createdAt: serverTimestamp() 
            });
            setNewCatName('');
            setIsCatModalOpen(false);
        } catch (error) {
            console.error("Erro ao salvar categoria:", error);
        }
    };

    const handleDeleteService = async (id) => {
        if (!window.confirm("Deseja realmente excluir este serviço?")) return;
        try {
            await deleteDoc(doc(db, collectionPath, id));
        } catch (error) {
            console.error("Erro ao excluir:", error);
        }
    };

    const formatCurrency = (val, period) => {
        let formatted = 'R$ 0,00';
        if (val) {
            if (val.toString().includes('R$')) {
                formatted = val;
            } else {
                const numeric = parseFloat(val.toString().replace(',', '.'));
                if (isNaN(numeric)) {
                    formatted = `R$ ${val}`;
                } else {
                    formatted = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(numeric);
                }
            }
        }

        const periodSuffixes = {
            'Mensal': ' /mês',
            'Anual': ' /ano',
            'Hora': ' /hora',
            'Equipamento': ' /equip.',
            'Único': ''
        };

        return `${formatted}${periodSuffixes[period] || ''}`;
    };

    const filteredServices = services.filter(service => {
        const matchesSearch = service.name.toLowerCase().includes(searchTerm.toLowerCase()) || 
                             service.description.toLowerCase().includes(searchTerm.toLowerCase());
        const matchesCategory = categoryFilter === 'Todas' || service.category === categoryFilter;
        return matchesSearch && matchesCategory;
    });

    const handleBlurValue = () => {
        let val = formData.value.trim();
        if (!val) return;

        // Remove R$ se o usuário tiver digitado manualmente para limpar o dado
        val = val.replace('R$', '').trim();
        
        // Substitui ponto por vírgula para padronizar entrada brasileira se necessário
        // mas o parseFloat precisa de ponto para converter
        const numeric = parseFloat(val.replace(',', '.'));
        
        if (!isNaN(numeric)) {
            // Formata com 2 casas decimais e volta para vírgula
            const formatted = numeric.toFixed(2).replace('.', ',');
            setFormData(prev => ({ ...prev, value: formatted }));
        }
    };

    return (
        <div className="p-6 space-y-6 animate-in fade-in duration-500">
            {/* Cabeçalho de Impressão (Exibido apenas ao imprimir) */}
            <div className="hidden print:block mb-8 border-b-2 border-slate-300 pb-4">
                <div className="flex justify-between items-end">
                    <div>
                        <div className="flex items-center gap-2 mb-1">
                            <div className="w-8 h-8 bg-cyan-600 rounded-lg flex items-center justify-center font-bold text-white shadow-lg">A</div>
                            <span className="text-xl font-bold text-slate-800">Intranet Arcus</span>
                        </div>
                        <h1 className="text-2xl font-bold text-slate-900">Tabela de Preços e Serviços</h1>
                        <p className="text-sm text-slate-500">Valores de serviços prestados vigentes.</p>
                    </div>
                    <div className="text-right text-xs text-slate-500">
                        <p>Gerado em {new Date().toLocaleDateString('pt-BR')} às {new Date().toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}</p>
                        {categoryFilter !== 'Todas' && <p>Filtro: Categoria - {categoryFilter}</p>}
                        {searchTerm && <p>Busca: "{searchTerm}"</p>}
                    </div>
                </div>
            </div>

            {/* Header Area */}
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 print:hidden">
                <div>
                    <h1 className="text-2xl font-bold text-white flex items-center gap-2">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-7 w-7 text-cyan-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        Tabela de Preços e Serviços
                    </h1>
                    <p className="text-slate-400 text-sm">Consulte e gerencie valores de serviços prestados.</p>
                </div>

                <div className="flex items-center gap-2">
                    <button 
                        onClick={() => window.print()}
                        className="bg-slate-700 hover:bg-slate-600 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-all shadow-lg border border-slate-600"
                        title="Imprimir Tabela"
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-cyan-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
                        </svg>
                        Imprimir
                    </button>

                    {canEdit && (
                        <button 
                            onClick={() => handleOpenServiceModal()}
                            className="bg-cyan-600 hover:bg-cyan-500 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-all shadow-lg shadow-cyan-900/20"
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                <path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd" />
                            </svg>
                            Novo Serviço
                        </button>
                    )}
                </div>
            </div>

            {/* Filters Bar */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 bg-slate-800/50 p-4 rounded-xl border border-slate-700 print:hidden">
                <div className="relative">
                    <input 
                        type="text" 
                        placeholder="Buscar por nome ou descrição..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="w-full bg-slate-900 border border-slate-700 rounded-lg py-2 pl-10 pr-4 text-white focus:outline-none focus:ring-2 focus:ring-cyan-500/50"
                    />
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 absolute left-3 top-2.5 text-slate-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                </div>

                <div className="flex gap-2">
                    <select 
                        value={categoryFilter}
                        onChange={(e) => setCategoryFilter(e.target.value)}
                        className="flex-1 bg-slate-900 border border-slate-700 rounded-lg py-2 px-4 text-white focus:outline-none focus:ring-2 focus:ring-cyan-500/50"
                    >
                        <option value="Todas">Todas as Categorias</option>
                        {categoriesList.map(cat => (
                            <option key={cat.id} value={cat.name}>{cat.name}</option>
                        ))}
                    </select>
                    {canEdit && (
                        <button 
                            onClick={() => setIsCatModalOpen(true)}
                            className="bg-slate-700 hover:bg-slate-600 text-cyan-400 p-2 rounded-lg border border-slate-600 transition-all flex items-center justify-center"
                            title="Nova Categoria"
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                            </svg>
                        </button>
                    )}
                </div>
            </div>

            {/* Table */}
            <div className="bg-slate-800 rounded-xl border border-slate-700 overflow-hidden shadow-xl">
                <div className="overflow-x-auto custom-scrollbar">
                    <table className="w-full text-left min-w-[900px] md:min-w-full">
                        <thead>
                            <tr className="bg-slate-900/50 border-b border-slate-700">
                                {canEdit && <th className="p-4 w-12 text-center text-slate-500 print:hidden">#</th>}
                                <th className="p-4 text-slate-400 font-bold uppercase text-xs tracking-wider print:text-slate-900">Serviço</th>
                                <th className="p-4 text-slate-400 font-bold uppercase text-xs tracking-wider print:text-slate-900">Categoria</th>
                                <th className="p-4 text-slate-400 font-bold uppercase text-xs tracking-wider print:text-slate-900">Descrição</th>
                                <th className="p-4 text-slate-400 font-bold uppercase text-xs tracking-wider print:text-slate-900">Valor</th>
                                <th className="p-4 text-slate-400 font-bold uppercase text-xs tracking-wider print:text-slate-900">Recorrência</th>
                                {canEdit && <th className="p-4 text-slate-400 font-bold uppercase text-xs tracking-wider text-right print:hidden">Ações</th>}
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-700/50">
                            {loading ? (
                                <tr><td colSpan={7} className="p-8 text-center text-slate-500">Carregando serviços...</td></tr>
                            ) : filteredServices.length === 0 ? (
                                <tr><td colSpan={7} className="p-8 text-center text-slate-500">Nenhum serviço encontrado.</td></tr>
                            ) : filteredServices.map((service, index) => (
                                <tr key={service.id} className="hover:bg-slate-700/30 transition-colors">
                                    {canEdit && (
                                        <td className="p-2 w-12 text-center bg-slate-900/10 print:hidden">
                                            <div className="flex flex-col items-center gap-1">
                                                <button 
                                                    onClick={() => handleMove(index, 'up')}
                                                    disabled={index === 0}
                                                    className="text-slate-600 hover:text-cyan-400 disabled:opacity-0 transition-colors"
                                                >
                                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M14.707 12.707a1 1 0 01-1.414 0L10 9.414l-3.293 3.293a1 1 0 01-1.414-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 010 1.414z" clipRule="evenodd"/></svg>
                                                </button>
                                                <button 
                                                    onClick={() => handleMove(index, 'down')}
                                                    disabled={index === filteredServices.length - 1}
                                                    className="text-slate-600 hover:text-cyan-400 disabled:opacity-0 transition-colors"
                                                >
                                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd"/></svg>
                                                </button>
                                            </div>
                                        </td>
                                    )}
                                    <td className="p-4 text-white font-medium print:text-slate-900">{service.name}</td>
                                    <td className="p-4">
                                        <span className="px-2 py-1 bg-slate-900 border border-slate-700 rounded text-[10px] font-bold text-cyan-400 uppercase tracking-tighter print:bg-slate-100 print:text-cyan-700 print:border-slate-300">
                                            {service.category || 'Geral'}
                                        </span>
                                    </td>
                                    <td className="p-4 text-slate-400 text-sm max-w-xs truncate print:text-slate-700 print:whitespace-normal print:max-w-none">{service.description}</td>
                                    <td className="p-4 font-mono text-emerald-400 font-bold whitespace-nowrap print:text-emerald-700">{formatCurrency(service.value, service.period)}</td>
                                    <td className="p-4 uppercase text-[10px] font-bold tracking-widest text-slate-500 whitespace-nowrap print:text-slate-600">
                                        {service.period || 'Único'}
                                    </td>
                                    {canEdit && (
                                        <td className="p-4 text-right space-x-2 print:hidden">
                                            <button onClick={() => handleOpenServiceModal(service)} className="text-slate-400 hover:text-cyan-400 transition-colors">
                                                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                                    <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
                                                </svg>
                                            </button>
                                            <button onClick={() => handleDeleteService(service.id)} className="text-slate-400 hover:text-red-400 transition-colors">
                                                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                                    <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
                                                </svg>
                                            </button>
                                        </td>
                                    )}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>


            {/* Modal de Serviço */}
            {isServiceModalOpen && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-slate-950/80 backdrop-blur-sm">
                    <div className="bg-slate-900 border border-slate-700 w-full max-w-md rounded-2xl shadow-2xl overflow-hidden animate-in zoom-in duration-300">
                        <div className="p-6 border-b border-slate-700 flex justify-between items-center">
                            <h2 className="text-xl font-bold text-white uppercase tracking-tighter">
                                {editingService ? 'Editar Serviço' : 'Novo Serviço'}
                            </h2>
                            <button onClick={() => setIsServiceModalOpen(false)} className="text-slate-500 hover:text-white">
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>
                        <form onSubmit={handleSaveService} className="p-6 space-y-4">
                            <div>
                                <label className="block text-xs font-bold text-slate-500 uppercase tracking-widest mb-1">Nome do Serviço</label>
                                <input 
                                    required value={formData.name}
                                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                                    className="w-full bg-slate-800 border border-slate-700 rounded-lg py-2 px-3 text-white focus:outline-none focus:ring-2 focus:ring-cyan-500/50"
                                />
                            </div>
                            
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-xs font-bold text-slate-500 uppercase tracking-widest mb-1">Categoria</label>
                                    <select 
                                        required value={formData.category}
                                        onChange={(e) => setFormData({...formData, category: e.target.value})}
                                        className="w-full bg-slate-800 border border-slate-700 rounded-lg py-2 px-3 text-white focus:outline-none focus:ring-2 focus:ring-cyan-500/50"
                                    >
                                        <option value="" disabled>Selecione...</option>
                                        {categoriesList.map(cat => (
                                            <option key={cat.id} value={cat.name}>{cat.name}</option>
                                        ))}
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-xs font-bold text-slate-500 uppercase tracking-widest mb-1">Recorrência</label>
                                    <select 
                                        value={formData.period}
                                        onChange={(e) => setFormData({...formData, period: e.target.value})}
                                        className="w-full bg-slate-800 border border-slate-700 rounded-lg py-2 px-3 text-white focus:outline-none focus:ring-2 focus:ring-cyan-500/50"
                                    >
                                        <option value="Único">Valor Único</option>
                                        <option value="Mensal">Mensal</option>
                                        <option value="Anual">Anual</option>
                                        <option value="Hora">Por Hora</option>
                                        <option value="Equipamento">Por Equipamento</option>
                                    </select>
                                </div>
                            </div>

                            <div>
                                <label className="block text-xs font-bold text-slate-500 uppercase tracking-widest mb-1">Valor (Formatado automaticamente)</label>
                                <input 
                                    required value={formData.value}
                                    onChange={(e) => setFormData({...formData, value: e.target.value})}
                                    onBlur={handleBlurValue}
                                    placeholder="Ex: 150,00"
                                    className="w-full bg-slate-800 border border-slate-700 rounded-lg py-2 px-3 text-white focus:outline-none focus:ring-2 focus:ring-cyan-500/50"
                                />
                            </div>
                            
                            <div>
                                <label className="block text-xs font-bold text-slate-500 uppercase tracking-widest mb-1">Descrição</label>
                                <textarea 
                                    required value={formData.description}
                                    onChange={(e) => setFormData({...formData, description: e.target.value})}
                                    rows="3"
                                    className="w-full bg-slate-800 border border-slate-700 rounded-lg py-2 px-3 text-white focus:outline-none focus:ring-2 focus:ring-cyan-500/50 resize-none"
                                ></textarea>
                            </div>
                            
                            <div className="pt-4 flex gap-3">
                                <button type="button" onClick={() => setIsServiceModalOpen(false)} className="flex-1 px-4 py-2 border border-slate-700 rounded-lg text-slate-400 font-bold hover:bg-slate-800 transition-all">Cancelar</button>
                                <button type="submit" className="flex-1 px-4 py-2 bg-cyan-600 rounded-lg text-white font-bold hover:bg-cyan-500 transition-all shadow-lg shadow-cyan-900/20">Salvar</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Modal de Categoria */}
            {isCatModalOpen && (
                <div className="fixed inset-0 z-[110] flex items-center justify-center p-4 bg-slate-950/80 backdrop-blur-sm">
                    <div className="bg-slate-900 border border-slate-700 w-full max-w-sm rounded-2xl shadow-2xl overflow-hidden animate-in zoom-in duration-300">
                        <div className="p-6 border-b border-slate-700 flex justify-between items-center">
                            <h2 className="text-lg font-bold text-white uppercase tracking-tighter">Nova Categoria</h2>
                            <button onClick={() => setIsCatModalOpen(false)} className="text-slate-500 hover:text-white">
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>
                        <form onSubmit={handleSaveCategory} className="p-6 space-y-4">
                            <div>
                                <label className="block text-xs font-bold text-slate-500 uppercase tracking-widest mb-1">Nome da Categoria</label>
                                <input 
                                    autoFocus
                                    required value={newCatName}
                                    onChange={(e) => setNewCatName(e.target.value)}
                                    className="w-full bg-slate-800 border border-slate-700 rounded-lg py-2 px-3 text-white focus:outline-none focus:ring-2 focus:ring-cyan-500/50"
                                />
                            </div>
                            <div className="pt-2 flex gap-3">
                                <button type="button" onClick={() => setIsCatModalOpen(false)} className="flex-1 px-4 py-2 border border-slate-700 rounded-lg text-slate-400 font-bold">Cancelar</button>
                                <button type="submit" className="flex-1 px-4 py-2 bg-cyan-600 rounded-lg text-white font-bold transition-all hover:bg-cyan-500">Cadastrar</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
}
